import React, { CSSProperties } from 'react';
import styled from 'styled-components';

import { FontStyle } from '@/utils/StyleUtil';
import StringUtil from '@/utils/StringUtil';
import { QNA_STATUS } from '@/models/ina';
import DividerDefault from '../Divider/Default';
import Image from 'next/future/image';
import { GetThumbnailUrl } from '@/queries/ina/thumbnail/getThumbnailUrl';
import { URLs } from '@/api';

interface IQuesitonInfo {
    questionId: number;
    status?: number;
    content: string;
    contentLine?: number;
    hasQuestionMark?: boolean;
    showStatus?: boolean;
    answerCount?: number;
    hasDivider?: boolean;
    isLast?: boolean;
    style?: CSSProperties;
    className?: string;
    contentTitle?: string;
}

const QnAInfoByStatus = ({
    questionId,
    status,
    content,
    contentLine,
    hasQuestionMark = true,
    showStatus = true,
    answerCount,
    hasDivider,
    isLast = false,
    style,
    className,
    contentTitle,
}: IQuesitonInfo) => {
    const { thumbnailUrl: thumbnailHash } = GetThumbnailUrl(questionId);
    const fileUrl = thumbnailHash ? URLs.temp.getFileUrl(encodeURIComponent(thumbnailHash)) : null;

    let iconBgColor;
    let iconTextColor;
    let statusText;
    let statusBoxColor;
    let statusBoxBorder;

    switch (status) {
        case QNA_STATUS.WAITING:
            iconBgColor = '#FFE4E4';
            iconTextColor = '#FF1E1E';
            statusText = '답변대기';
            statusBoxColor = '#FFEFEF';
            statusBoxBorder = '#FDEAEA';
            break;
        case QNA_STATUS.COMPLETE:
            iconBgColor = '#EAFAFF';
            iconTextColor = '#00ACE2';
            statusText = '답변완료';
            statusBoxColor = '#EAFAFF';
            statusBoxBorder = '#E3F3F8';
            break;
        case QNA_STATUS.UNANSWERED:
            iconBgColor = '#F0F0F0';
            iconTextColor = '#ACACAC';
            statusText = '미답변';
            statusBoxColor = '#F3F4F4';
            statusBoxBorder = '#E4E4E4';
            break;
        case QNA_STATUS.ANSWER:
            iconBgColor = '#eafffe';
            iconTextColor = '#ACACAC';
            statusText = '';
            statusBoxColor = '#FFEFEF';
            statusBoxBorder = '#FDEAEA';
            break;
    }

    const cutOff: number = fileUrl ? 24 : 32;

    const contentCuttOff = content.length > cutOff ? content.substring(0, cutOff) + '...' : content;

    const contentTitleCutOff = contentTitle
        ? contentTitle.length > cutOff
            ? contentTitle.substring(0, cutOff) + '...'
            : contentTitle
        : contentCuttOff;

    return (
        <div>
            <ContentContainer className={className} style={style} isAnswerPage={contentLine !== undefined}>
                <FlexContainer contentLine={contentLine}>
                    <StatusContainer
                        iconTextColor={iconTextColor}
                        statusBoxColor={statusBoxColor}
                        statusBoxBorder={statusBoxBorder}
                    >
                        <div>
                            {showStatus && (
                                <div className="answer_flex">
                                    <div className="answer_status_box">
                                        <div className="status_text">{statusText}</div>
                                    </div>
                                    {answerCount !== undefined && (
                                        <div className="answer_cnt">{answerCount}개의 답변</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </StatusContainer>
                    <QuestionContent contentLine={contentLine}>
                        <div className="title">{contentTitle}</div>
                        <div className="content">{content}</div>
                    </QuestionContent>
                </FlexContainer>
                {fileUrl && (
                    <ThumbnailContainer>
                        <img alt="thumbnail" width={60} height={60} src={fileUrl} />
                    </ThumbnailContainer>
                )}
            </ContentContainer>
            {hasDivider && !isLast && (
                <DividerDefault
                    color="#f1f1f1"
                    thickness={1}
                    length="calc(100% - 40px)"
                    style={{ marginLeft: '20px' }}
                />
            )}
        </div>
    );
};

const ContentContainer = styled.div<{ isAnswerPage: boolean }>`
    ${({ theme }) => theme.flex.flexOnly};
    padding: 22px 20px 20px 20px;

    //overflow: hidden;
    //text-overflow: ellipsis;

    &:active {
        ${({ isAnswerPage }) => isAnswerPage && `background-color: #fbfbfb`};
    }
`;

const IconContainer = styled.div<{ iconTextColor?: string; iconBgColor?: string }>`
    ${({ theme }) => theme.flex.flexOnly};
    ${({ theme }) => theme.flex.justifyCenter};
    position: relative;
    width: 22px;
    height: 22px;
    margin-right: 10px;

    .icon_text {
        ${FontStyle(17, 20, '500', undefined, -1)}
        color: ${({ iconTextColor }) => iconTextColor};
        z-index: 1;
    }

    .icon_background {
        width: 22px;
        height: 10px;
        background-color: ${({ iconBgColor }) => iconBgColor};
        position: absolute;
        margin-top: 12px;
        z-index: 0;
    }
`;

const FlexContainer = styled.div<{ contentLine?: number }>`
    ${({ theme }) => theme.flex.flexColumn};
    flex: 1;
    overflow: hidden;
    .content_text {
        ${FontStyle(14, 22, '400', '#444444', -1)}
        ${({ theme, contentLine }) => theme.settingByBrowser.lineClamp(contentLine)}
        white-space: pre-wrap;
    }
`;

const QuestionContent = styled.h2<{ contentLine?: number }>`
    //${({ theme, contentLine }) => theme.settingByBrowser.lineClamp(contentLine)}
    //white-space: pre-wrap;
    margin-top: 12px;
    margin-bottom: 0;
    padding-right: 12px;
    ${({ theme }) => theme.flex.flexColumn};
    gap: 6px;

    .title {
        ${FontStyle(15, 23, '400', '#444444', -1)}

        overflow: hidden; /* Hide overflowing content */
        text-overflow: '...';
        white-space: nowrap;
    }

    .content {
        ${FontStyle(12, 20, '400', '#ACACAC', -1)}
        overflow: hidden; /* Hide overflowing content */
        text-overflow: '...';
        white-space: nowrap;
    }
`;

const ThumbnailContainer = styled.div`
    align-self: center;
    height: 60px;
    width: 60px;
    overflow: hidden;
    border-radius: 6px;
`;

const StatusContainer = styled.div<{ iconTextColor?: string; statusBoxColor?: string; statusBoxBorder?: string }>`
    ${({ theme }) => theme.flex.flexOnly};
    ${({ theme }) => theme.flex.justifyBetween};
    margin-top: 1px;

    .answer_flex {
        ${({ theme }) => theme.flex.flexOnly};
        ${({ theme }) => theme.flex.justifyCenter};
        gap: 5px;
        .answer_status_box {
            ${({ theme }) => theme.flex.flexOnly};
            ${({ theme }) => theme.flex.justifyCenter};
            padding: 2px 8px;
            background-color: ${({ statusBoxColor }) => statusBoxColor};
            border-radius: 12px;
            border-width: 1px;
            border-style: solid;
            border-color: ${({ statusBoxBorder }) => statusBoxBorder};
            .status_text {
                ${FontStyle(12, 20, '500', undefined, -1)}
                color: ${({ iconTextColor }) => iconTextColor}
            }

            .vertical_divider {
                width: 1px;
                height: 10px;
                margin: 0 6px;
                background-color: #ececec;
            }
        }

        .answer_cnt {
            ${FontStyle(12, 20, '400', '#686868', -1)}
        }
    }
    .created_at {
        ${FontStyle(12, 20, '400', '#c8c8c8', -1)}
    }
`;

export default QnAInfoByStatus;
